import { Suspense, useCallback, useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { Box } from '@factorialco/deprecated-design-system'
import { Spinner } from '@factorialco/f0-react/dist/experimental'

import { MarketplaceCartSidepanel } from 'modules/it_management/components/device_catalog/MarketplaceCartSidepanel'
import { DeviceCatalogCartProvider } from 'modules/it_management/contexts/DeviceCatalogCartContext'
import { useDeviceCatalogCartContext } from 'modules/it_management/contexts/DeviceCatalogCartContext'
import { useDeviceCatalogHeaderActions } from 'modules/it_management/hooks/device_catalog/useDeviceCatalogHeaderActions'
import { useItManagementFeatureAvailability } from 'modules/it_management/hooks/useItManagementFeatureAvailability'
import CollectionsBootstrap from 'modules/it_management/lib/CollectionsBootstrap'

const CART_HASH = '#cart'

const Container = () => {
  const location = useLocation()
  const {
    employee,
    access: { id: accessId },
  } = useCurrentSession()
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console -- temporary debug for detail page
    console.log('[DeviceCatalogRoot] Container render', { pathname: location.pathname })
  }
  return (
    <Outlet
      context={{
        currentUserId: accessId,
        employee,
      }}
    />
  )
}

const ORDER_PLAN_PATH = '/device-catalog/order-plan'

const CartSidepanelWithCheckout = () => {
  const navigate = useNavigate()
  const cart = useDeviceCatalogCartContext()
  const { company, access } = useCurrentSession()

  const handleCheckout = useCallback(() => {
    cart.closeCart()
    navigate(ORDER_PLAN_PATH)
  }, [cart, navigate])

  const handleCartRemoveItem = useCallback(
    (variantId: string, offerId: string) => {
      track(trackingEvents.itManagement.deviceCatalogEvents.removeFromCart, {
        device_id: variantId,
        company_id: company.id,
        user_id: access.id,
      })
      cart.removeItem(variantId, offerId)
    },
    [cart, company.id, access.id]
  )

  const handleCartUpdateQuantity = useCallback(
    (variantId: string, offerId: string, quantity: number) => {
      track(trackingEvents.itManagement.deviceCatalogEvents.updateCartQuantity, {
        device_id: variantId,
        new_quantity: quantity,
        company_id: company.id,
        user_id: access.id,
      })
      cart.updateQuantity(variantId, offerId, quantity)
    },
    [cart, company.id, access.id]
  )

  return (
    <MarketplaceCartSidepanel
      isOpen={cart.isOpen}
      items={cart.items}
      subtotal={cart.subtotal}
      total={cart.total}
      itemCount={cart.itemCount}
      onClose={cart.closeCart}
      onUpdateQuantity={handleCartUpdateQuantity}
      onRemoveItem={handleCartRemoveItem}
      onCheckout={handleCheckout}
      isCheckingOut={false}
    />
  )
}

const HashCartOpener = () => {
  const cart = useDeviceCatalogCartContext()
  const { pathname, hash } = useLocation()
  const navigate = useNavigate()
  const { company, access } = useCurrentSession()

  useEffect(() => {
    if (hash === CART_HASH) {
      track(trackingEvents.itManagement.deviceCatalogEvents.openCart, {
        item_count: cart.itemCount,
        company_id: company.id,
        user_id: access.id,
      })
      cart.openCart()
      navigate(pathname, { replace: true })
    }
  }, [hash, pathname, navigate, cart, company.id, access.id])

  return null
}

const DeviceCatalogHeaderActions = () => {
  useDeviceCatalogHeaderActions()
  return null
}

const DeviceCatalogPageFallback = () => (
  <Box justifyContent="center" alignItems="center" height="full">
    <Spinner size="large" />
  </Box>
)

const DeviceCatalogRootPage = () => {
  const {
    isItManagementEnabled,
    loading: featureLoading,
    error: featureError,
  } = useItManagementFeatureAvailability()

  return (
    <DeviceCatalogCartProvider>
      <DeviceCatalogHeaderActions />
      <HashCartOpener />
      <CartSidepanelWithCheckout />
      {featureLoading ? (
        <DeviceCatalogPageFallback />
      ) : (
        <Suspense fallback={<DeviceCatalogPageFallback />}>
          <AuthorizeUser authorizeFn={() => !featureError && isItManagementEnabled}>
            <CollectionsBootstrap>
              <Container />
            </CollectionsBootstrap>
          </AuthorizeUser>
        </Suspense>
      )}
    </DeviceCatalogCartProvider>
  )
}

export default DeviceCatalogRootPage
